// Formatters ported from Reference/CCap-3/CCap-3/static/app.js (kept byte-compatible in output).

export const fmtBytes = (b: number | null | undefined): string => {
  if (!b && b !== 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
};

/** `1d 3h`, `14h 22m`, or `mm:ss` — the old app's three-tier duration. */
export const fmtDur = (s: number | null | undefined): string => {
  let n = Math.max(0, Math.floor(s || 0));
  const d = Math.floor(n / 86400);
  const hh = Math.floor((n % 86400) / 3600);
  const mm = Math.floor((n % 3600) / 60);
  const ss = n % 60;
  n = 0;
  return d ? `${d}d ${hh}h` : hh ? `${hh}h ${mm}m` : `${mm}:${String(ss).padStart(2, '0')}`;
};

/** `hh:mm:ss` clock-style duration (REC counter, transcript). */
export const fmtClock = (s: number): string => {
  const n = Math.max(0, Math.floor(s));
  const z = (x: number) => String(x).padStart(2, '0');
  return `${z(Math.floor(n / 3600))}:${z(Math.floor((n % 3600) / 60))}:${z(n % 60)}`;
};

export const fmtKbps = (v: number | null | undefined): string =>
  v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)} Mb/s` : `${Math.round(v)} kb/s`;

/** ts in unix seconds (as the old API), or an ISO string. */
export const fmtAgo = (ts: number | string | null | undefined, now = Date.now()): string => {
  if (!ts) return 'never';
  const t = typeof ts === 'string' ? Date.parse(ts) / 1000 : ts;
  const s = Math.max(0, now / 1000 - t);
  return s < 5 ? 'just now' : s < 60 ? `${Math.round(s)} s ago` : s < 3600 ? `${Math.round(s / 60)} min ago` : s < 86400 ? `${Math.round(s / 3600)} h ago` : `${Math.round(s / 86400)} d ago`;
};

export const semver = (v: string | null | undefined): [number, number, number] | null => {
  const m = /^v?(\d+)\.(\d+)\.(\d+)/.exec(String(v || '').trim());
  return m ? [+m[1]!, +m[2]!, +m[3]!] : null;
};

export const isNewer = (a: string, b: string): boolean => {
  const x = semver(a);
  const y = semver(b);
  if (!x) return false;
  if (!y) return true;
  for (let i = 0; i < 3; i++) if (x[i] !== y[i]) return x[i]! > y[i]!;
  // same core: a pre-release (2.4.2-rc1) is older than the release (2.4.2)
  const pa = /-/.test(a);
  const pb = /-/.test(b);
  return pb && !pa;
};

export const compareSemver = (a: string, b: string): number => (isNewer(a, b) ? 1 : isNewer(b, a) ? -1 : 0);

export const fmtDbfs = (v: number | null | undefined): string => (v == null || v <= -60 ? '−∞' : `−${Math.abs(Math.round(v))}`);

export const fmtMs = (v: number | null | undefined): string => (v == null ? '—' : `${Math.round(v)} ms`);

/** `16 Oct 2026 · 09:02` as in the design tables. */
export const fmtDateTime = (iso: string | null | undefined, locale = 'en-GB'): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  const date = d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} · ${time}`;
};

export const fmtUtcClock = (d = new Date()): string => {
  const z = (x: number) => String(x).padStart(2, '0');
  return `${z(d.getUTCHours())}:${z(d.getUTCMinutes())}:${z(d.getUTCSeconds())}`;
};

export const fmtPct = (v: number): string => `${Math.round(v)} %`;
